import { pxToDP, useIntl } from '@capsicum/common';
import { BackButton, Button, InteractionLayer, Text, TOPBAR_ZINDEX } from '@capsicum/common-web';
import { AddIcon } from '@capsicum/common-web/icons';
import {
	type ChooseYourTeamSelection,
	type ChooseYourTeamsRef,
	type PartnerWithImageMapAndExtraFields,
	type SelectFavouriteTeamValue,
	useChooseYourTeams
} from '@capsicum/socios-core';
import { forwardRef, useCallback, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import { KeyboardAwareBottomWrapper } from '../../../auth/KeyboardAwareBottomWrapper';
import { LoaderActivities } from '../../../feeds/ui/LoaderActivities';
import { useTeamsWidthCalculation } from '../../hooks/useTeamsWidthCalculation';
import { SelectFavouriteTeam as SelectFavouriteTeamV2 } from '../select-favourite-team/SelectFavouriteTeam';

import { PageWrapper } from './PageWrapper';
import { TeamSkeleton } from './SelectFavouriteTeam.skeletons';
import { BottomButtonsContent, BottomButtonsWrapper, WidgetWrapper } from './SelectFavouriteTeam.styled';
import { Team } from './Team';

type ChooseYourTeamsCommonProps = {
	onSubmit: () => void;
	preselection?: ChooseYourTeamSelection;
};

type ChooseYourTeamsPageProps = ChooseYourTeamsCommonProps & {
	layout: 'page';
};

type ChooseYourTeamsWidgetProps = ChooseYourTeamsCommonProps & {
	layout: 'widget';
	onSkip: () => void;
};

type ChooseYourTeamsProps = ChooseYourTeamsPageProps | ChooseYourTeamsWidgetProps;

const TEAM_HEIGHT = 126;

export const ChooseYourTeams = forwardRef<ChooseYourTeamsRef, ChooseYourTeamsProps>(
	(props: ChooseYourTeamsProps, ref) => {
		const { layout, onSubmit, preselection } = props;

		const {
			spacings: { xxsmall }
		} = useTheme();

		const { $t } = useIntl();

		const Wrapper = layout === 'page' ? PageWrapper : WidgetWrapper;

		const {
			closeSelector,
			favouritePartner,
			followedPartners,
			isLoading,
			isSubmittable,
			onChange,
			onPersist,
			openedSelector,
			openFavouriteSelector,
			openFollowSelector,
			value: selectorValue
		} = useChooseYourTeams({ LoaderContent: LoaderActivities, onSubmit, preselection, ref });

		const resultsGap = pxToDP(xxsmall);
		const { elementRef, isSmall, teamWidth } = useTeamsWidthCalculation({
			getTeamsPerRow: useCallback((width: number) => (width > 520 ? 5 : 4), []),
			resultsGap
		});

		const getTeamJSX = useCallback(
			({
				isFavourite = false,
				partner
			}: {
				isFavourite?: boolean;
				partner: PartnerWithImageMapAndExtraFields;
			}) => {
				return (
					<S.StyledTeam
						$width={teamWidth}
						image={partner.images?.partnerLogo?.url}
						isFavourite={isFavourite}
						isSmall={isSmall}
						key={partner?.id}
						name={partner.name}
					/>
				);
			},
			[isSmall, teamWidth]
		);

		const favouriteResult = useMemo(() => {
			return isLoading ? (
				<TeamSkeleton height={TEAM_HEIGHT} width={teamWidth} />
			) : favouritePartner ? (
				getTeamJSX({ isFavourite: true, partner: favouritePartner })
			) : (
				<S.AddIconWrapper
					$center
					$width={teamWidth}
					testID="chooseYourTeams-openFavouriteTeamSelector-button"
					onClick={openFavouriteSelector}
				>
					<AddIcon />
				</S.AddIconWrapper>
			);
		}, [isLoading, teamWidth, getTeamJSX, favouritePartner, openFavouriteSelector]);

		const followedResults = useMemo(() => {
			if (isLoading) {
				return Array(8)
					.fill(null)
					.map((_, index) => <TeamSkeleton height={TEAM_HEIGHT} key={index} width={teamWidth} />);
			}

			const teams = followedPartners.reduce<Array<JSX.Element>>((partners, partner) => {
				const { id: partnerID } = partner;

				if (partnerID) partners.push(getTeamJSX({ partner }));

				return partners;
			}, []);

			teams.unshift(
				<S.AddIconWrapper
					$center={teams.length === 0}
					$width={teamWidth}
					key="add"
					testID="chooseYourTeams-openFollowTeamSelector-button"
					onClick={openFollowSelector}
				>
					<AddIcon />
				</S.AddIconWrapper>
			);

			return teams;
		}, [getTeamJSX, followedPartners, openFollowSelector, teamWidth, isLoading]);

		const onChoose = useCallback(
			(payload: SelectFavouriteTeamValue) => {
				onChange(payload);
				closeSelector();
			},
			[closeSelector, onChange]
		);

		const actions = useMemo(() => {
			if (props.layout === 'page' || isSubmittable) {
				return (
					<Button
						disabled={!isSubmittable}
						testID="chooseYourTeams-submit-button"
						text={$t('global.button.done')}
						onClick={onPersist}
					/>
				);
			}

			return (
				<Button
					testID="chooseYourTeams-skip-button"
					text={$t('chooseYourTeams.actions.skip')}
					variant="outlined"
					onClick={props.onSkip}
				/>
			);
		}, [$t, props, onPersist, isSubmittable]);

		return (
			<>
				{layout === 'widget' && (
					<S.Header>
						{openedSelector && <BackButton testID="chooseYourTeams-back-button" onClick={closeSelector} />}
					</S.Header>
				)}

				{openedSelector ? (
					<SelectFavouriteTeamV2
						hasTopbar
						initialValue={selectorValue}
						layout={layout}
						persist={false}
						selection={openedSelector}
						onSubmit={onChoose}
					/>
				) : (
					<>
						<Wrapper>
							<S.Content>
								<S.Section>
									<Text variant="title5">{$t('chooseYourTeams.favourite.title')}</Text>

									<S.SectionContent $center $gap={resultsGap}>
										{favouriteResult}
									</S.SectionContent>
								</S.Section>

								<S.Section>
									<Text variant="title5">{$t('chooseYourTeams.follow.title')}</Text>

									<S.SectionContent
										$center={followedResults.length === 1}
										$gap={resultsGap}
										ref={elementRef}
									>
										{followedResults}
									</S.SectionContent>
								</S.Section>
							</S.Content>
						</Wrapper>

						<KeyboardAwareBottomWrapper>
							<BottomButtonsWrapper $isPage={layout === 'page'}>
								<S.StyledBottomButtonsContent>{actions}</S.StyledBottomButtonsContent>
							</BottomButtonsWrapper>
						</KeyboardAwareBottomWrapper>
					</>
				)}
			</>
		);
	}
);

ChooseYourTeams.displayName = 'ChooseYourTeams';

namespace S {
	export const Header = styled.div`
		top: 0;
		width: 100%;
		position: sticky;
		z-index: ${TOPBAR_ZINDEX};
		padding: ${({ theme }) => theme.spacings.small};
		flex: 0 0 ${({ theme }) => theme.sizing.xxlarge};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};

		border-style: ${({ theme }) => theme.borderStyles.divider.style};
		border-width: ${({ theme }) => theme.borderStyles.divider.width};
		border-color: ${({ theme }) => theme.colors.borders.main.divider};
	`;

	export const Content = styled.div`
		flex: 1;
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
		padding-top: ${({ theme }) => theme.spacings.xlarge};
		padding-left: ${({ theme }) => theme.spacings.medium};
		padding-right: ${({ theme }) => theme.spacings.medium};
		padding-bottom: ${({ theme }) => theme.spacings.medium};

		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			margin-bottom: ${({ theme }) => theme.spacings.xxxlarge};
		}
	`;

	export const Section = styled.section`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const SectionContent = styled.div<{ $center: boolean; $gap: number }>`
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		${({ theme }) => theme.border.radius.medium};
		gap: ${({ theme }) => theme.spacings.large} ${({ $gap }) => $gap}px;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
	`;

	export const StyledTeam = styled(Team)<{ $width?: number }>`
		overflow: hidden;
		height: ${TEAM_HEIGHT}px;
		padding: ${({ theme }) => theme.spacings.xsmall} 0;
		flex: 0 0 ${({ $width }) => ($width ? $width : 100)}px;
	`;

	export const AddIconWrapper = styled(InteractionLayer)<{ $center: boolean; $width?: number }>`
		display: flex;
		justify-content: center;
		height: ${TEAM_HEIGHT}px;
		padding: ${({ theme }) => theme.spacings.xsmall} 0;
		flex: 0 0 ${({ $width }) => ($width ? $width : 100)}px;
		align-items: ${({ $center }) => ($center ? 'center' : 'flex-start')};
	`;

	export const StyledBottomButtonsContent = styled(BottomButtonsContent)`
		justify-content: center;
	`;
}
